/**
 * HowItWorks — collapsible demo panel for the AI Card Grader.
 *
 * Open by default on first visit (tracked via localStorage key
 * 'grader.demoSeen'). Subsequent visits start collapsed so power users
 * aren't annoyed.
 *
 * Three visual steps:
 *   1. Frame the card — animated SVG showing the bounding box overlay.
 *   2. Hold steady — auto-capture fires when sharp.
 *   3. Repeat with two tilts — glare-free surface check.
 *
 * Pure presentational; no props required.
 */
import React, { useEffect, useState } from 'react';

const SEEN_KEY = 'grader.demoSeen';

export default function HowItWorks() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(SEEN_KEY) !== '1'; }
    catch { return true; }
  });

  useEffect(() => {
    if (!open) {
      try { localStorage.setItem(SEEN_KEY, '1'); } catch {}
    }
  }, [open]);

  return (
    <div style={{
      maxWidth: 720, margin: '0 auto 16px', padding: 12,
      border: '1px solid #c7d2fe', background: '#eef2ff', borderRadius: 8,
    }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#1e40af', padding: 0, fontSize: 15, fontWeight: 600,
          width: '100%', textAlign: 'left',
        }}
        aria-expanded={open}
      >
        {open ? '▾' : '▸'} How it works — 60-second walkthrough
      </button>

      {open && (
        <div style={{ marginTop: 12 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
          }}>
            <Step n={1} title="Frame the card">
              <FrameDemo />
              <p style={stepText}>
                Line your card up inside the box. The border turns
                <span style={{ color: '#22c55e', fontWeight: 600 }}> green </span>
                when the focus is sharp.
              </p>
            </Step>
            <Step n={2} title="Hold steady">
              <TiltDemo angle={0} caption="Flat" />
              <p style={stepText}>
                When 4 frames in a row are sharp, we auto-capture. Or tap{' '}
                <strong>📸 Capture</strong> to take it manually.
              </p>
            </Step>
            <Step n={3} title="Two more angles">
              <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                <TiltDemo angle={-15} caption="Left" />
                <TiltDemo angle={ 15} caption="Right" />
              </div>
              <p style={stepText}>
                Tilt left ~15°, capture, then right ~15°. Glare moves with the
                angle but real defects don't, which lets us suppress false
                positives in the surface check.
              </p>
            </Step>
            <Step n={4} title="Confirm each shot">
              <ConfirmDemo />
              <p style={stepText}>
                After every capture you'll see a still preview. Hit
                <strong> ✓ Use this </strong>or<strong> ↻ Retake</strong>{' '}
                — nothing is submitted until you approve all six.
              </p>
            </Step>
          </div>

          <div style={{ marginTop: 12, fontSize: 13, color: '#374151' }}>
            <strong>Tips for best results:</strong>
            <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
              <li>Bright, even lighting — avoid direct overhead glare on holos.</li>
              <li>Plain dark background works best (lets the framer find the card edges).</li>
              <li>Phone camera or any webcam works. No app install required.</li>
              <li>Don't have a camera? Tap <strong>Switch to file upload</strong>.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e0e7ff', borderRadius: 6,
      padding: 12, display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          background: '#4f46e5', color: '#fff', borderRadius: '50%',
          width: 22, height: 22, display: 'inline-grid', placeItems: 'center',
          fontSize: 12, fontWeight: 700,
        }}>{n}</span>
        <strong style={{ fontSize: 14 }}>{title}</strong>
      </div>
      {children}
    </div>
  );
}

const stepText = { fontSize: 12, color: '#4b5563', margin: 0 };

// SVG demos -----------------------------------------------------------

function FrameDemo() {
  return (
    <svg viewBox="0 0 100 80" style={{ width: '100%', height: 90 }}>
      <rect width="100" height="80" fill="#0f172a" rx="3" />
      {/* card cutout shows green when "in frame" */}
      <rect x="35" y="14" width="30" height="52" rx="2" fill="none" stroke="#22c55e" strokeWidth="0.8" />
      {/* corner brackets */}
      {[[35,14],[65,14],[35,66],[65,66]].map(([x,y],i)=>(
        <g key={i} stroke="#22c55e" strokeWidth="1" strokeLinecap="round">
          <line x1={x + (i%2?-3:3)} y1={y} x2={x} y2={y} />
          <line x1={x} y1={y + (i<2?3:-3)} x2={x} y2={y} />
        </g>
      ))}
      {/* tiny card glyph centered */}
      <rect x="38" y="18" width="24" height="44" rx="1" fill="#475569" opacity="0.5" />
    </svg>
  );
}

function TiltDemo({ angle, caption }) {
  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <svg viewBox="0 0 60 80" style={{ width: '100%', height: 80 }}>
        <rect width="60" height="80" fill="#0f172a" rx="3" />
        <g transform={`rotate(${angle} 30 40)`}>
          <rect x="20" y="14" width="20" height="52" rx="2" fill="#94a3b8" />
          <rect x="22" y="20" width="16" height="40" rx="1" fill="#334155" />
        </g>
      </svg>
      <div style={{ fontSize: 11, color: '#4b5563', marginTop: 2 }}>{caption}</div>
    </div>
  );
}

function ConfirmDemo() {
  return (
    <svg viewBox="0 0 100 80" style={{ width: '100%', height: 90 }}>
      <rect width="100" height="80" fill="#0f172a" rx="3" />
      <rect x="35" y="10" width="30" height="52" rx="2" fill="#94a3b8" />
      <rect x="38" y="14" width="24" height="44" rx="1" fill="#334155" />
      <rect x="3" y="66" width="44" height="11" rx="2" fill="#16a34a" />
      <text x="25" y="74" textAnchor="middle" fontSize="6" fill="#fff" fontWeight="700">✓ Use this</text>
      <rect x="53" y="66" width="44" height="11" rx="2" fill="#fff" stroke="#9ca3af" strokeWidth="0.5" />
      <text x="75" y="74" textAnchor="middle" fontSize="6" fill="#374151" fontWeight="600">↻ Retake</text>
    </svg>
  );
}
